import {
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { clientsApi, publicConsentApi } from "@/api/clients";
import type {
  ClientsQueryParams,
  ConfirmPublicConsentPayload,
  ConsentChannel,
  ConsentRequest,
  CreateClientPayload,
  MergeClientsPayload,
  PaperConsentPayload,
} from "@/types/clients";

// ============================
// Query Key Factories
// ============================

export const clientKeys = {
  all: ["clients"] as const,
  lists: () => [...clientKeys.all, "list"] as const,
  list: (params: ClientsQueryParams) =>
    [...clientKeys.lists(), params] as const,
  detail: (clientId: string) =>
    [...clientKeys.all, "detail", clientId] as const,
  person: (clientId: string) =>
    [...clientKeys.all, "person", clientId] as const,
  duplicates: (agentId?: string) =>
    [...clientKeys.all, "duplicates", agentId ?? ""] as const,
  merges: () => [...clientKeys.all, "merges"] as const,
  request: (requestId: string) =>
    [...clientKeys.all, "request", requestId] as const,
  publicConsent: (token: string) => ["public-consent", token] as const,
};

// ============================
// Clients
// ============================

export function useClients(params: ClientsQueryParams) {
  return useQuery({
    queryKey: clientKeys.list(params),
    queryFn: () => clientsApi.getClients(params),
    staleTime: 15 * 1000,
    placeholderData: (previousData) => previousData,
  });
}

export function useClientDetails(clientId: string | null) {
  return useQuery({
    queryKey: clientKeys.detail(clientId ?? ""),
    queryFn: () => clientsApi.getClient(clientId ?? ""),
    enabled: clientId !== null,
  });
}

/** Audited server-side; the result is never cached (gcTime 0) and the UI hides it again after a while. */
export function useRevealSensitiveData() {
  return useMutation({
    mutationFn: (clientId: string) => clientsApi.revealSensitiveData(clientId),
    gcTime: 0,
  });
}

/** Administrators: the same person's records and consents across all agents. */
export function useClientPerson(clientId: string | null) {
  return useQuery({
    queryKey: clientKeys.person(clientId ?? ""),
    queryFn: () => clientsApi.getClientPerson(clientId ?? ""),
    enabled: clientId !== null,
  });
}

export function useAssignAgent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      clientId,
      agentId,
    }: {
      clientId: string;
      agentId: string;
    }) => clientsApi.assignAgent(clientId, agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

/** Which channels the backend can send through (SMS stays off until a gateway is configured). */
export function useConsentChannels() {
  return useQuery({
    queryKey: [...clientKeys.all, "channels"] as const,
    queryFn: () => clientsApi.getConsentChannels(),
    staleTime: 5 * 60_000,
  });
}

export function useUploadPaperConsent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      clientId,
      payload,
    }: {
      clientId: string;
      payload: PaperConsentPayload;
    }) => clientsApi.uploadPaperConsent(clientId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

export function useCreateClient() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateClientPayload) =>
      clientsApi.createClient(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
    },
  });
}

// ============================
// Data quality: duplicates, merging, name casing
// ============================

export function useDuplicateClients(agentId?: string, enabled = true) {
  return useQuery({
    queryKey: clientKeys.duplicates(agentId),
    queryFn: () => clientsApi.getDuplicates(agentId),
    enabled,
    staleTime: 30 * 1000,
  });
}

export function useClientMerges(enabled = true) {
  return useQuery({
    queryKey: clientKeys.merges(),
    queryFn: () => clientsApi.getMerges(),
    enabled,
  });
}

export function useMergeClients() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: MergeClientsPayload) =>
      clientsApi.mergeClients(payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

/** Administrators: preview (apply: false) or save the name casing clean-up. */
export function useFixNameCasing() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (apply: boolean) => clientsApi.fixNameCasing(apply),
    onSuccess: (_data, apply) => {
      if (apply) {
        queryClient.invalidateQueries({ queryKey: clientKeys.all });
      }
    },
  });
}

// ============================
// Consent requests
// ============================

export function useRequestConsent() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      clientId,
      includesHealth,
      channel,
    }: {
      clientId: string;
      includesHealth: boolean;
      channel?: ConsentChannel;
    }) => clientsApi.requestConsent(clientId, includesHealth, channel),
    onSuccess: (request) => {
      queryClient.setQueryData(clientKeys.request(request.id), request);
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

/** Polls while pending so the agent sees a confirmation made by the client via the link. */
export function useConsentRequest(requestId: string | null) {
  const queryClient = useQueryClient();
  return useQuery({
    queryKey: clientKeys.request(requestId ?? ""),
    queryFn: async () => {
      const previous = queryClient.getQueryData<ConsentRequest>(
        clientKeys.request(requestId ?? ""),
      );
      const request = await clientsApi.getConsentRequest(requestId ?? "");
      // The client may have confirmed via the link in the meantime — refresh the list's ✓.
      if (previous?.status === "Pending" && request.status !== "Pending") {
        queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
      }
      return request;
    },
    enabled: requestId !== null,
    refetchInterval: (query) =>
      query.state.data?.status === "Pending" ? 3000 : false,
  });
}

export function useVerifyConsentCodes() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      requestId,
      codes,
    }: {
      requestId: string;
      codes: string[];
    }) => clientsApi.verifyCodes(requestId, codes),
    onSettled: (_data, _error, { requestId }) => {
      queryClient.invalidateQueries({
        queryKey: clientKeys.request(requestId),
      });
      queryClient.invalidateQueries({ queryKey: clientKeys.lists() });
    },
  });
}

export function useCancelConsentRequest() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (requestId: string) => clientsApi.cancelRequest(requestId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: clientKeys.all });
    },
  });
}

// ============================
// Public consent page
// ============================

export function publicConsentQueryOptions(token: string) {
  return queryOptions({
    queryKey: clientKeys.publicConsent(token),
    queryFn: () => publicConsentApi.get(token),
    retry: false,
    staleTime: Number.POSITIVE_INFINITY,
  });
}

export function usePublicConsent(token: string) {
  return useQuery(publicConsentQueryOptions(token));
}

export function useConfirmPublicConsent(token: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: ConfirmPublicConsentPayload) =>
      publicConsentApi.confirm(token, payload),
    onSuccess: (data) => {
      queryClient.setQueryData(clientKeys.publicConsent(token), data);
    },
  });
}
